<script setup lang="ts">
import Drawer from "primevue/drawer";
import Tag from "primevue/tag";

import EventDetail from "./EventDetail.vue";
import SeverityBadge from "./SeverityBadge.vue";
import { eventFinalSeverity, type QualityEvent } from "../api";

const props = defineProps<{
  event: QualityEvent | null;
  visible: boolean;
  email: string | null;
  isManager: boolean;
}>();

const emit = defineEmits<{
  (e: "update:visible", v: boolean): void;
  (e: "refresh", eventId: number): void;
}>();

function onVisibleUpdate(v: boolean): void {
  emit("update:visible", v);
}
function onChanged(): void {
  if (props.event) emit("refresh", props.event.id);
}
</script>

<template>
  <Drawer
    :visible="visible"
    @update:visible="onVisibleUpdate"
    position="right"
    :style="{ width: 'min(720px, 95vw)' }"
  >
    <template #header>
      <div class="drawer-header">
        <div class="drawer-title">
          <span v-if="event" class="drawer-id" :data-testid="'qe-id-' + event.id">
            QE-{{ event.id }}
          </span>
          <span class="drawer-title-text">{{ event ? event.summary : "Event" }}</span>
        </div>
        <div class="drawer-sub">standalone quality event</div>
      </div>
    </template>
    <div v-if="!event">No event selected.</div>
    <template v-else>
      <div class="head-band">
        <SeverityBadge
          :severity="event.severity"
          :final-severity="eventFinalSeverity(event)"
          prefix="Severity"
        />
        <Tag
          :value="event.status"
          :severity="
            event.status === 'closed' ? 'success' :
            event.status === 'disputed' ? 'danger' :
            'secondary'
          "
        />
      </div>
      <EventDetail
        :event="event"
        :email="email"
        :is-manager="isManager"
        @changed="onChanged"
      />
    </template>
  </Drawer>
</template>

<style scoped>
.drawer-header {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.drawer-title {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-weight: 700;
  font-size: 16px;
  min-width: 0;
}
.drawer-title-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.drawer-id {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  font-weight: 600;
  color: var(--p-surface-600);
  background: var(--p-surface-100);
  padding: 2px 8px;
  border-radius: 4px;
  flex-shrink: 0;
  letter-spacing: 0.02em;
}
.drawer-sub {
  font-size: 12px;
  color: var(--p-surface-500);
}
.head-band {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 12px;
}
</style>
